import React, { useState, useEffect, useMemo } from 'react';
import FallbackCard from './FallbackCard';

/**
 * SmartImage Component
 * 
 * Displays an image, but falls back to a generated SVG pattern
 * if the image fails to load or if src is empty.
 * The fallback variant is consistently selected based on the alt text.
 * 
 * @param {string} src - Image source URL
 * @param {string} alt - Alt text
 * @param {string} className - CSS classes
 * @param {number} fallbackMaxLength - Max char length for fallback card title (default: 15)
 */
const SmartImage = ({ 
  src, 
  alt = '', 
  className = '', 
  fallbackMaxLength = 15,
  ...props 
}) => {
  const [error, setError] = useState(false);

  // Reset error state when src changes
  useEffect(() => {
    setError(false);
  }, [src]);

  const handleError = () => {
    setError(true);
  };

  // Select a consistent fallback variant (0, 1, 2) based on alt text
  const fallbackVariant = useMemo(() => {
    if (!alt) return 0;
    let hash = 0;
    for (let i = 0; i < alt.length; i++) {
      hash = alt.charCodeAt(i) + ((hash << 5) - hash);
    }
    return Math.abs(hash) % 3; // 3 variants
  }, [alt]);

  // Check if we should show the fallback
  // 1. No src provided
  // 2. Image failed to load
  if (!src || error) {
    return (
      <div className={`${className} relative overflow-hidden`}>
         <FallbackCard 
            variant={fallbackVariant}
            title={alt}
            maxLength={fallbackMaxLength}
            className="w-full h-full"
         />
      </div>
    );
  }

  return (
    <img 
      src={src} 
      alt={alt} 
      className={className}
      onError={handleError}
      {...props}
    />
  );
};

export default SmartImage;
